/* Webinars -> Failures and Retry Queue.
 *
 * Provider-registration failures, WhatsApp failures, CTA failures and
 * voice-follow-up failures (plus scheduled webinar jobs that exhausted
 * the scheduler's own retries), each with its explicit failure reason.
 *
 * Idempotency: a retry button is disabled while its request is in flight
 * AND the backend refuses a second retry on its own — registration retry
 * is a compare-and-set claim (webinar.confirm_registration /
 * start_external_registration), message retry requeues only an item that
 * is still failed and re-checks every webinar switch first (409
 * otherwise). Voice follow-ups are never manually re-dispatched: the AI
 * Voice pipeline owns their bounded retry.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TestBadge } from "@/components/common";
import { AlertTriangle, RefreshCcw, CheckCircle2 } from "lucide-react";
import { CampaignPicker, toneClass, useWebinarCaps } from "@/components/webinar/webinarShared";

/* Splits the /webinar/failures payload into the page's sections. Exported
 * for tests. */
export const groupFailures = (data) => {
  const msgs = data?.message_failures?.items || [];
  const failedMsgs = msgs.filter((m) => m.status_bucket !== "skipped");
  return {
    registration: data?.registration_failures?.items || [],
    whatsapp: failedMsgs.filter((m) => m.category === "whatsapp"),
    cta: failedMsgs.filter((m) => m.category === "cta"),
    voice: failedMsgs.filter((m) => m.category === "voice"),
    skipped: msgs.filter((m) => m.status_bucket === "skipped"),
    jobs: data?.job_failures?.items || [],
  };
};

const Section = ({ title, count, testId, children, hint }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-heading flex items-center gap-2">
        {count ? <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))]" /> : <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />}
        {title} ({count})
      </CardTitle>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </CardHeader>
    <CardContent className="space-y-2">
      {count === 0 ? <p className="text-xs text-muted-foreground">Nothing failed.</p> : children}
    </CardContent>
  </Card>
);

const Who = ({ row }) => (
  <span className="text-xs">
    {row.contact_id
      ? <Link to={`/customers/${row.contact_id}`} className="font-medium hover:underline">{row.contact_name || row.contact_id}</Link>
      : <span className="font-mono">{(row.enrollment_id || row.related_id || "").slice(0, 8)}</span>}
    {row.campaign_name && <span className="text-muted-foreground"> · {row.campaign_name}</span>}
    {row.is_test_record && <> <TestBadge show /></>}
  </span>
);

const MessageRow = ({ m, canConfig, busy, onRetry }) => (
  <div className="p-2 rounded-md border flex items-start gap-2 flex-wrap text-xs" data-testid={`webinar-failure-msg-${m.outbox_id}`}>
    <div className="flex-1 min-w-[220px] space-y-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Who row={m} />
        <Badge variant="outline" className="text-[10px]">{m.automation_label || m.kind || m.provider}</Badge>
        <Badge variant="outline" className={`text-[10px] ${toneClass(m.status_bucket === "failed" ? "danger" : "muted")}`}>
          {(m.status || "").replace(/_/g, " ")}
        </Badge>
        {m.attempts > 0 && <span className="text-[10px] text-muted-foreground">{m.attempts} attempts</span>}
        <span className="text-[10px] text-muted-foreground">{fmtIST(m.created_at)}</span>
      </div>
      <p className="text-[hsl(var(--danger))] break-words" data-testid={`webinar-failure-reason-${m.outbox_id}`}>
        {m.last_error || "No error text recorded"}
      </p>
      {!m.retryable && m.retry_blocked_reason && (
        <p className="text-[10px] text-muted-foreground">Retry unavailable: {m.retry_blocked_reason}</p>
      )}
    </div>
    {canConfig && m.retryable && (
      <Button size="sm" variant="outline" disabled={busy} data-testid={`webinar-retry-msg-${m.outbox_id}`}
              onClick={() => onRetry(m)}>
        <RefreshCcw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Retry
      </Button>
    )}
  </div>
);

export const WebinarFailures = ({ campaigns, campaignId, onCampaignChange, refreshKey }) => {
  const { canConfig } = useWebinarCaps();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [showSkipped, setShowSkipped] = useState(false);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ limit: "200" });
      if (campaignId) qs.set("campaign_id", campaignId);
      setData((await api.get(`/webinar/failures?${qs}`)).data);
    } catch (e) { toast.error(errDetail(e)); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // A ref, not state: two clicks in the same tick both see the pre-render
  // state, so only a synchronously-updated set can stop the second one.
  // (The backend would still turn a racing second retry into a no-op.)
  const inFlight = useRef(new Set());
  const guarded = async (id, fn) => {
    if (inFlight.current.has(id)) return; // never two in-flight retries for one row
    inFlight.current.add(id);
    setBusy((b) => ({ ...b, [id]: true }));
    try { await fn(); } finally {
      inFlight.current.delete(id);
      setBusy((b) => ({ ...b, [id]: false }));
      load();
    }
  };

  const retryRegistration = (r) => guarded(r.enrollment_id, async () => {
    try {
      const res = await api.post(`/webinar/enrollments/${r.enrollment_id}/retry-registration`,
                                 { reason: "manual retry from Webinars → Failures" });
      const st = res.data?.registration_state;
      if (st === "registration_failed" || (st === "external_registration_pending" && res.data?.retry_state === "retryable")) {
        toast.error(`Still failing: ${res.data?.last_error || "provider error"}`);
      } else toast.success(`Registration now ${(st || "").replace(/_/g, " ")}`);
    } catch (e) { toast.error(errDetail(e)); }
  });

  const retryMessage = (m) => guarded(m.outbox_id, async () => {
    try {
      const res = await api.post(`/webinar/outbox/${m.outbox_id}/retry`, { reason: "manual retry from Webinars → Failures" });
      if (res.data?.requeued) toast.success("Message requeued for delivery");
      else toast.message("Already requeued — nothing to do");
    } catch (e) { toast.error(errDetail(e)); }
  });

  const g = groupFailures(data);
  const regTotal = data?.registration_failures?.total ?? 0;

  return (
    <div className="space-y-4" data-testid="webinar-failures">
      <div className="flex items-center gap-2 flex-wrap">
        <CampaignPicker campaigns={campaigns} value={campaignId} onChange={onCampaignChange} allowAll
                        testId="webinar-failures-campaign" />
        <Button size="sm" variant="ghost" onClick={load}><RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
        {data && (
          <span className="text-[11px] text-muted-foreground">
            {regTotal + (data.message_failures?.totals_by_bucket?.failed || 0) + (data.job_failures?.total || 0)} failed ·{" "}
            {data.message_failures?.totals_by_bucket?.skipped || 0} skipped
          </span>
        )}
      </div>
      {!data ? null : (
        <>
          <Section title="Provider registration failures" count={regTotal} testId="webinar-failures-registration"
                   hint="Failed API registrations and shared-link invitations that could not be queued. Retry is safe: a concurrent or repeated retry never registers twice or sends a second link.">
            {g.registration.map((r) => (
              <div key={r.enrollment_id} className="p-2 rounded-md border flex items-start gap-2 flex-wrap text-xs"
                   data-testid={`webinar-failure-reg-${r.enrollment_id}`}>
                <div className="flex-1 min-w-[220px] space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Who row={r} />
                    <Badge variant="outline" className="text-[10px]">{r.registration_method === "external_link" ? "shared link" : "provider API"}</Badge>
                    <Badge variant="outline" className="text-[10px]">{r.attempts} attempts</Badge>
                    {r.dead_lettered && <Badge variant="outline" className={`text-[10px] ${toneClass("danger")}`}>dead-letter</Badge>}
                    <span className="text-[10px] text-muted-foreground">{fmtIST(r.failed_at)}</span>
                  </div>
                  <p className="text-[hsl(var(--danger))] break-words">{r.error || "No error text recorded"}</p>
                </div>
                {canConfig && (
                  <Button size="sm" variant="outline" disabled={!!busy[r.enrollment_id]}
                          data-testid={`webinar-retry-reg-${r.enrollment_id}`} onClick={() => retryRegistration(r)}>
                    <RefreshCcw className={`h-3 w-3 mr-1 ${busy[r.enrollment_id] ? "animate-spin" : ""}`} /> Retry
                  </Button>
                )}
              </div>
            ))}
            {regTotal > g.registration.length && (
              <p className="text-[11px] text-muted-foreground">Showing {g.registration.length} of {regTotal}.</p>
            )}
          </Section>
          <Section title="WhatsApp message failures" count={g.whatsapp.length} testId="webinar-failures-whatsapp"
                   hint="Retry re-checks the master switch, campaign status, cancellation and opt-out before requeueing.">
            {g.whatsapp.map((m) => <MessageRow key={m.outbox_id} m={m} canConfig={canConfig} busy={!!busy[m.outbox_id]} onRetry={retryMessage} />)}
          </Section>
          <Section title="CTA failures" count={g.cta.length} testId="webinar-failures-cta"
                   hint="Post-webinar CTA messages that failed to send.">
            {g.cta.map((m) => <MessageRow key={m.outbox_id} m={m} canConfig={canConfig} busy={!!busy[m.outbox_id]} onRetry={retryMessage} />)}
          </Section>
          <Section title="Voice follow-up failures" count={g.voice.length} testId="webinar-failures-voice"
                   hint="Shown for visibility only — the AI Voice pipeline retries these itself; a manual re-dispatch could place a duplicate call.">
            {g.voice.map((m) => <MessageRow key={m.outbox_id} m={m} canConfig={canConfig} busy={false} onRetry={() => {}} />)}
          </Section>
          <Section title="Scheduled job failures" count={data.job_failures?.total ?? 0} testId="webinar-failures-jobs"
                   hint="Reminder / nurture / session-finalise / voice jobs that failed 3 times in the durable scheduler. Read-only.">
            {g.jobs.map((j) => (
              <div key={j.job_id} className="p-2 rounded-md border text-xs space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">{j.automation_label}</Badge>
                  <span className="font-mono text-[10px]">{(j.related_id || "").slice(0, 8)}</span>
                  <span className="text-[10px] text-muted-foreground">{j.attempts} attempts · {fmtIST(j.run_at)}</span>
                  {j.is_test_record && <TestBadge show />}
                </div>
                <p className="text-[hsl(var(--danger))] break-words">{j.last_error || "No error text recorded"}</p>
                <p className="text-[10px] text-muted-foreground">{j.retry_blocked_reason}</p>
              </div>
            ))}
          </Section>
          <Card data-testid="webinar-failures-skipped">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-heading">Skipped (held, not failed) ({g.skipped.length})</CardTitle>
              <label className="flex items-center gap-2 text-xs">
                <Switch checked={showSkipped} onCheckedChange={setShowSkipped} data-testid="webinar-failures-show-skipped" /> Show
              </label>
            </CardHeader>
            {showSkipped && (
              <CardContent className="space-y-2">
                <p className="text-[11px] text-muted-foreground">
                  TEST records while TEST sends are blocked, or messages held because the provider was off in Integrations.
                </p>
                {g.skipped.length === 0 ? <p className="text-xs text-muted-foreground">Nothing skipped.</p>
                  : g.skipped.map((m) => <MessageRow key={m.outbox_id} m={m} canConfig={canConfig} busy={!!busy[m.outbox_id]} onRetry={retryMessage} />)}
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default WebinarFailures;
